import { useState, useCallback, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Star, Volume2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useSpeech } from "@/hooks/useSpeech";

// ─── Animal Sounds Game ───
const animalData = [
  { emoji: "🐱", name: "قطة", sound: "القطة تقول مياو مياو مياو", soundText: "مواء" },
  { emoji: "🐶", name: "كلب", sound: "الكلب يقول هو هو هو هو", soundText: "نباح" },
  { emoji: "🐓", name: "ديك", sound: "الديك يقول كوكو كوكو كوكو", soundText: "صياح" },
  { emoji: "🐑", name: "خروف", sound: "الخروف يقول ماء ماء ماء", soundText: "ثغاء" },
  { emoji: "🐄", name: "بقرة", sound: "البقرة تقول مووو مووو مووو", soundText: "خوار" },
  { emoji: "🐸", name: "ضفدع", sound: "الضفدع يقول نق نق نق نق", soundText: "نقيق" },
  { emoji: "🦁", name: "أسد", sound: "الأسد يقول أررر أررر أررر", soundText: "زئير" },
  { emoji: "🐦", name: "عصفور", sound: "العصفور يقول زق زق زق زق", soundText: "تغريد" },
  { emoji: "🐴", name: "حصان", sound: "الحصان يقول صهيل صهيل صهيل", soundText: "صهيل" },
  { emoji: "🐝", name: "نحلة", sound: "النحلة تقول بز بز بز بز", soundText: "طنين" },
];

interface Props {
  character: string;
  characterName: string;
  onFinish: (score: number, maxScore: number) => void;
  onCorrect: () => void;
}

export default function AnimalSoundsGame({ character, characterName, onFinish, onCorrect }: Props) {
  const { speak } = useSpeech();
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [currentAnimal, setCurrentAnimal] = useState(0);
  const [options, setOptions] = useState<typeof animalData>([]);
  const [usedAnimals, setUsedAnimals] = useState<number[]>([]);
  const maxRounds = 7;

  const generateRound = useCallback((used: number[]) => {
    const available = animalData.map((_, i) => i).filter(i => !used.includes(i));
    if (available.length === 0) return;
    const chosen = available[Math.floor(Math.random() * available.length)];
    setCurrentAnimal(chosen);
    // Generate 3 wrong + 1 correct
    const others = animalData.filter((_, i) => i !== chosen).sort(() => Math.random() - 0.5).slice(0, 3);
    setOptions([animalData[chosen], ...others].sort(() => Math.random() - 0.5));
    setSelectedAnswer(null);
    setIsCorrect(null);
    setUsedAnimals([...used, chosen]);
  }, []);

  useEffect(() => {
    generateRound([]);
  }, []);

  const playSound = () => {
    speak(animalData[currentAnimal].sound);
  };

  const handleAnswer = (animal: typeof animalData[0]) => {
    const correct = animal.name === animalData[currentAnimal].name;
    setSelectedAnswer(animal.name);
    setIsCorrect(correct);
    if (correct) {
      setScore(s => s + 1);
      onCorrect();
    }
    setTimeout(() => {
      const nextRound = round + 1;
      if (nextRound >= maxRounds) {
        onFinish(score + (correct ? 1 : 0), maxRounds);
      } else {
        setRound(nextRound);
        generateRound(usedAnimals);
      }
    }, 1500);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
      <Progress value={((round + 1) / maxRounds) * 100} className="h-3 rounded-full" />
      <Card className="bg-white/90 backdrop-blur-sm border-2 border-lime-200 shadow-xl rounded-2xl">
        <CardContent className="p-5 space-y-4 text-center">
          <div className="flex items-center justify-center gap-3">
            <img src={character} alt={characterName} className="w-14 h-14 rounded-full border-2 border-lime-300 bg-lime-50 object-cover" />
            <p className="text-sm font-semibold text-lime-700">{characterName} يسألك: من هذا الحيوان؟</p>
          </div>
          
          {/* Sound button */}
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={playSound}
            className="mx-auto flex flex-col items-center gap-2 bg-gradient-to-br from-lime-400 to-green-500 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow"
          >
            <Volume2 className="w-10 h-10" />
            <span className="text-lg font-bold">🔊 اسمع الصوت!</span>
          </motion.button>

          <p className="text-sm text-muted-foreground font-medium">اضغط الزر ثم اختر الحيوان الصحيح</p>

          <div className="grid grid-cols-2 gap-3">
            {options.map((animal) => {
              const isSelected = selectedAnswer === animal.name;
              const isCorrectOpt = animal.name === animalData[currentAnimal].name;
              let cls = "bg-lime-50 border-lime-200 text-lime-800 hover:bg-lime-100";
              if (selectedAnswer) {
                if (isCorrectOpt) cls = "bg-green-200 border-green-400 text-green-900";
                else if (isSelected) cls = "bg-red-200 border-red-400 text-red-900";
              }
              return (
                <button key={animal.name} onClick={() => !selectedAnswer && handleAnswer(animal)}
                  disabled={!!selectedAnswer}
                  className={`p-4 rounded-xl border-2 transition-all active:scale-95 ${cls}`}>
                  <span className="text-4xl block mb-1">{animal.emoji}</span>
                  <span className="text-sm font-bold">{animal.name}</span>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
